// ================================================================
// src/hooks/useBooksEngine.js
// ================================================================
// لایه‌ی اتصال React به هسته‌ی books
// کاملاً روی storage + data + logic سوار می‌شه
// ----------------------------------------------------------------
// مسئولیت‌ها:
//   ۱. Provider مرکزی (BooksEngineProvider)
//   ۲. Base hooks (useEngine, useEngineTick, usePermission)
//   ۳. Data hooks (teacher/request/project/version/bookData)
//   ۴. Action hooks (workflow/version/admin با loading + toast)
//   ۵. Draft editing (auto-save + undo/redo + shortcuts)
//   ۶. Media hooks (blobUrl + upload)
//   ۷. Notification hooks
//   ۸. System hooks (storage, multi-tab, online)
//   ۹. Migration hooks
//  ۱۰. Admin hooks (diff, impact, audit)
// ================================================================

import React, {
  createContext, useContext, useEffect, useMemo, useRef,
  useState, useCallback, useSyncExternalStore,
} from 'react';

import { startPayment, PAYMENT_PURPOSE } from '../services/paymentService.js';

import { engine, utils, constants } from '../core/books/storage.js';
import dataEngine, {
  REQUEST_STATUS, PROJECT_STATE, VERSION_STATUS,
  GRADE_TITLES, SUBJECT_LABELS, SUBJECTS_BY_GRADE,
  normalizeGrade, normalizeSubject, normalizeField,   // 🔽 CHANGED
  // 🔽 CHANGED: curriculum helpers
  FIELDS, FIELD_LIST, FIELD_IDS, SUBJECTS,
  getGrade, getField, getSubject,
  getSubjectTitle, getBookTitle,
  isHighSchool, needsField,
  getSubjectIdsFor, getSubjectsFor, getStageForGrade,
} from '../core/books/data.js';
import {
  // 🔽 CHANGED: مستقیم از curriculum برای UI helpers
  getGradeOptions as getCurriculumGradeOptions,
  getGradeOptionsGrouped,
  getFieldOptions as getCurriculumFieldOptions,
  getSubjectOptions as getCurriculumSubjectOptions,
  STAGE_LIST,
} from '../core/education/curriculum.js';
import logicEngine, {
  NOTIF_TYPE, AUDIT_ACTION,
  notificationBus, auditLog, permissionGuard,
  rateLimiter,
  diffEngine, impactAnalyzer, progressManager,
  subscriptionManager,   // 🆕
  versionManager, workflowEngine, rollbackManager,
} from '../core/books/logic.js';

// 🆕 برای دسترسی به ExamContext
import { useExamsOptional } from '../components/Exams/ExamContext.jsx';

// ================================================================
// SECTION 1: CONSTANTS & SAFE DEFAULTS
// ================================================================

const RENDER_THROTTLE_MS = 50;

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

const REGISTRY_EVENTS = [
  'registry:teacher:changed',
  'registry:request:changed',
  'registry:project:changed',
  'registry:version:changed',
];

const DEFAULT_TOAST = (message, type = 'info') => {
  if (typeof console === 'undefined') return;
  const fn = type === 'error' ? console.error
    : type === 'warning' ? console.warn
    : console.log;
  fn.call(console, `[Toast:${type}] ${message}`);
};
const DEFAULT_CONFIRM = async () => true;

// ================================================================
// SECTION 2: BOOKS ENGINE CONTEXT
// ================================================================

const BooksEngineContext = createContext(null);

/**
 * Provider مرکزی سیستم کتاب‌ها.
 * باید در ریشه‌ی اپ (داخل AuthProvider و ToastProvider) قرار بگیرد.
 *
 * @param {object} props
 * @param {object|null} props.user        - از useAuth() گرفته می‌شود
 * @param {function}    props.toast       - از useToast() گرفته می‌شود
 * @param {function}    props.confirm     - async (options) => boolean
 * @param {function}    props.onReady     - callback وقتی engine آماده شد
 */
export function BooksEngineProvider({
  user = null,
  toast = DEFAULT_TOAST,
  confirm = DEFAULT_CONFIRM,
  onReady = null,
  children,
}) {
  const [ready, setReady] = useState(engine.isReady());
  const [initError, setInitError] = useState(null);
  const readyRef = useRef(ready);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!engine.isReady()) await engine.init();
        // در useEffect مربوط به init
if (!engine.isReady()) await engine.init();

// 🆕 اجرای migration
try {
  const { runStepQuizMigration } = await import('../core/books/migrations/stepQuizMigration.js');
  runStepQuizMigration();
} catch (e) {
  console.warn('[BooksEngineProvider] step quiz migration failed:', e);
}
        if (cancelled) return;

        // اجرای migration اگر لازم است
        if (dataEngine.migration.isNeeded()) {
          const legacy = readLegacyData();
          if (legacy) {
            try {
              await dataEngine.migration.migrate(legacy);
            } catch (e) {
              console.warn('[BooksEngineProvider] migration failed:', e);
            }
          }
        }

        setReady(true);
        readyRef.current = true;
        if (onReady) onReady({ engine, dataEngine, logicEngine });
      } catch (e) {
        console.error('[BooksEngineProvider] init failed:', e);
        setInitError(e);
      }
    })();

    return () => { cancelled = true; };
  }, [onReady]);

  const ctxValue = useMemo(
    () => ({
      engine,
      data: dataEngine,
      logic: logicEngine,
      user,
      toast,
      confirm,
      ready,
      initError,
    }),
    [ready, initError, user, toast, confirm]
  );

  return (
    <BooksEngineContext.Provider value={ctxValue}>
      {children}
    </BooksEngineContext.Provider>
  );
}

/** دسترسی به context با guard */
export function useBooksEngine() {
  const ctx = useContext(BooksEngineContext);
  if (!ctx) {
    throw new Error('useBooksEngine must be used within BooksEngineProvider');
  }
  return ctx;
}

/** نسخه‌ی نرم (بدون throw) برای کامپوننت‌های عمومی */
export function useBooksEngineOptional() {
  return useContext(BooksEngineContext);
}

// ================================================================
// SECTION 3: BASE HOOKS
// ================================================================

/**
 * تیک React که با یک رویداد engine تغییر می‌کند.
 * برای re-render پس از تغییرات.
 */
export function useEngineTick(eventOrEvents, deps = []) {
  const events = useMemo(
    () => (Array.isArray(eventOrEvents) ? eventOrEvents : [eventOrEvents]),
    [Array.isArray(eventOrEvents) ? eventOrEvents.join('|') : eventOrEvents, ...deps]
  );

  const [tick, setTick] = useState(0);   // ✅ مقدار برگشتی
  const lastEmitRef = useRef(0);
  const pendingRef = useRef(false);

  useEffect(() => {
    const throttled = () => {
      const nowMs = Date.now();
      if (nowMs - lastEmitRef.current < RENDER_THROTTLE_MS) {
        if (!pendingRef.current) {
          pendingRef.current = true;
          setTimeout(() => {
            pendingRef.current = false;
            lastEmitRef.current = Date.now();
            setTick((t) => t + 1);
          }, RENDER_THROTTLE_MS);
        }
        return;
      }
      lastEmitRef.current = nowMs;
      setTick((t) => t + 1);
    };

    const unsubs = events.filter(Boolean).map((ev) => engine.on(ev, throttled));
    return () => unsubs.forEach((u) => { try { u && u(); } catch {} });
  }, [events]);

  return tick;   // ✅ برگرداندن
}

/** بررسی دسترسی کاربر جاری */
export function usePermission(action, context = EMPTY_OBJECT) {
  const { user } = useBooksEngine();
  return useMemo(
    () => permissionGuard.can(user, action, context),
    
    [user, action, JSON.stringify(context)]
  );
}

/** capabilities محیط */
export function useCapabilities() {
  return engine.capabilities;
}

/** بررسی آماده بودن engine */
export function useEngineReady() {
  const { ready, initError } = useBooksEngine();
  return { ready, initError };
}

// ================================================================
// SECTION 4: DATA HOOKS
// ================================================================

export function useTeacherProfile(teacherId) {
  const tick = useEngineTick('registry:teacher:changed');
  return useMemo(
    () => (teacherId ? dataEngine.teacher.get(teacherId) : null),
    [teacherId, tick]
  );
}

export function useTeacherByUserId(userId) {
  const tick = useEngineTick('registry:teacher:changed');
  return useMemo(
    () => (userId ? dataEngine.teacher.getByUserId(userId) : null),
    [userId, tick]
  );
}

export function useTeacherByFolder(folderName) {
  const tick = useEngineTick('registry:teacher:changed');
  return useMemo(
    () => (folderName ? dataEngine.teacher.getByFolderName(folderName) : null),
    [folderName, tick]
  );
}

export function useTeachersForSubject(grade, subject, field = null) {
  const tick = useEngineTick(['registry:project:changed', 'registry:teacher:changed']);
  return useMemo(() => {
    if (!grade || !subject) return EMPTY_ARRAY;
    return dataEngine.query.getTeachersForSubject(grade, subject, field);
  }, [grade, subject, field, tick]);
}

export function useMyRequests() {
  const { user } = useBooksEngine();
  const tick = useEngineTick('registry:request:changed');
  return useMemo(() => {
    if (!user?.id) return EMPTY_ARRAY;
    const byUser = dataEngine.request.listByUser(user.id);
    const tp = dataEngine.teacher.getByUserId(user.id);
    const byTeacher = tp ? dataEngine.request.listByTeacher(tp.id) : [];
    const merged = [...byUser, ...byTeacher];
    const seen = new Set();
    return merged.filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    });
  }, [user?.id, tick]);
}

export function useRequest(requestId) {
  const tick = useEngineTick('registry:request:changed');
  return useMemo(
    () => (requestId ? dataEngine.request.get(requestId) : null),
    [requestId, tick]
  );
}

export function usePendingRequests() {
  const tick = useEngineTick('registry:request:changed');
  return useMemo(
    () => dataEngine.request.list({ status: [REQUEST_STATUS.PENDING, REQUEST_STATUS.EDIT_REQUESTED] }),
    [tick]
  );
}

export function useMyProjects() {
  const { user } = useBooksEngine();
  const tick = useEngineTick('registry:project:changed');
  return useMemo(() => {
    if (!user?.id) return EMPTY_ARRAY;
    const tp = dataEngine.teacher.getByUserId(user.id);
    if (!tp) return EMPTY_ARRAY;
    return dataEngine.project.listByTeacher(tp.id, { type: 'book' }); // 🆕 فقط کتاب‌ها
  }, [user?.id, tick]);
}

export function useProject(projectId) {
  const tick = useEngineTick('registry:project:changed');
  return useMemo(
    () => (projectId ? dataEngine.project.get(projectId) : null),
    [projectId, tick]
  );
}

export function useAllProjects(filter = EMPTY_OBJECT) {
  const tick = useEngineTick('registry:project:changed');
  const key = JSON.stringify(filter);
  return useMemo(() => {
    // 🆕 پیش‌فرض: فقط کتاب‌ها (سازگاری با قبل)
    const effective = filter.type === undefined
      ? { ...filter, type: 'book' }
      : filter;
    return dataEngine.project.list(effective);
  }, [key, tick]);
}

export function useProjectVersions(projectId, filter = EMPTY_OBJECT) {
  const tick = useEngineTick('registry:version:changed');
  const key = JSON.stringify(filter);
  return useMemo(
    () => (projectId ? dataEngine.version.listByProject(projectId, filter) : EMPTY_ARRAY),
    [projectId, key, tick]
  );
}

export function useVersion(versionId) {
  const tick = useEngineTick('registry:version:changed');
  return useMemo(
    () => (versionId ? dataEngine.version.get(versionId) : null),
    [versionId, tick]
  );
}

export function useBookData(grade, subject, folderName, field = null) {
  const tick = useEngineTick(['registry:project:changed', 'registry:version:changed']);
  return useMemo(() => {
    if (!grade || !subject || !folderName) return null;
    return dataEngine.query.getBookData(grade, subject, folderName, field);
  }, [grade, subject, folderName, field, tick]);
}

export function useLessonData(grade, subject, folderName, legacyLessonId, field = null) {
  const tick = useEngineTick(['registry:project:changed', 'registry:version:changed']);
  return useMemo(() => {
    if (!grade || !subject || !folderName || !legacyLessonId) return null;
    return dataEngine.query.getLessonData(grade, subject, folderName, legacyLessonId, field);
  }, [grade, subject, folderName, legacyLessonId, field, tick]);
}

export function useSystemStats() {
  const tick = useEngineTick(REGISTRY_EVENTS);
  return useMemo(() => dataEngine.query.getSystemStats(), [tick]);
}

// ================================================================
// 🔽 NEW: SECTION 4.5 — CURRICULUM HOOKS
// ================================================================
// هوک‌های آماده برای UI: پایه‌ها، رشته‌ها، کتاب‌ها
// همه از curriculum.js می‌آن (کاملاً reactive نیستن چون static هستن)
// ================================================================

/**
 * لیست گزینه‌های پایه (۱ تا ۱۲) برای dropdown
 * @param {object} opts - { includeAll: boolean, allLabel: string }
 * @returns {Array<{ value: string, label: string, group: string }>}
 */
export function useGradeOptions(opts = EMPTY_OBJECT) {
  return useMemo(() => getCurriculumGradeOptions(opts), [JSON.stringify(opts)]);
}

/**
 * پایه‌ها به‌صورت گروه‌بندی‌شده بر اساس مقطع
 * @returns {Array<{ stageId, stageTitle, options: Array<{ value, label }> }>}
 */
export function useGradeOptionsGrouped() {
  return useMemo(() => getGradeOptionsGrouped(), []);
}

/**
 * لیست گزینه‌های رشته برای یک پایه
 * @param {string|number} grade
 * @param {object} opts
 * @returns {Array<{ value: string, label: string }>} — خالی برای پایه‌های ۱-۹
 */
export function useFieldOptions(grade, opts = EMPTY_OBJECT) {
  const key = JSON.stringify(opts);
  return useMemo(() => {
    if (!grade) return EMPTY_ARRAY;
    return getCurriculumFieldOptions(grade, opts);
  }, [grade, key]);
}

/**
 * لیست گزینه‌های کتاب/درس برای یک پایه (و رشته برای ۱۰-۱۲)
 * @param {string|number} grade
 * @param {string|null} field
 * @param {object} opts - { includeAll, allLabel }
 * @returns {Array<{ value: string, label: string }>}
 */
export function useSubjectOptions(grade, field = null, opts = EMPTY_OBJECT) {
  const key = JSON.stringify(opts);
  return useMemo(() => {
    if (!grade) return EMPTY_ARRAY;
    return getCurriculumSubjectOptions(grade, field, opts);
  }, [grade, field, key]);
}

/**
 * اطلاعات کامل یک پایه
 * @returns {{ id, title, shortTitle, stage, hasFields, order } | null}
 */
export function useGradeInfo(grade) {
  return useMemo(() => (grade ? getGrade(grade) : null), [grade]);
}

/**
 * اطلاعات یک رشته
 * @returns {{ id, title, shortTitle } | null}
 */
export function useFieldInfo(field) {
  return useMemo(() => (field ? getField(field) : null), [field]);
}

/**
 * اطلاعات یک درس (شناسه یا عنوان)
 * @returns {{ id, title, shortTitle } | null}
 */
export function useSubjectInfo(subject) {
  return useMemo(() => (subject ? getSubject(subject) : null), [subject]);
}

/**
 * عنوان کامل کتاب — با شماره + رشته
 * مثال: 'فارسی (۱) — پایه دهم (ریاضی)'
 */
export function useBookTitle(grade, field, subject) {
  return useMemo(() => {
    if (!grade || !subject) return '';
    return getBookTitle(grade, field, subject);
  }, [grade, field, subject]);
}

/**
 * آیا این پایه نیاز به انتخاب رشته داره؟
 * @returns {boolean}
 */
export function useNeedsField(grade) {
  return useMemo(() => needsField(grade), [grade]);
}

/**
 * لیست مقاطع تحصیلی (برای نمایش در sidebar/menu)
 */
export function useStages() {
  return useMemo(() => STAGE_LIST, []);
}

// ================================================================
// SECTION 5: ACTION HOOKS (Loading + Error + Toast wrapper)
// ================================================================

// ================================================================
// SECTION 5: ACTION HOOKS (Loading + Error + Toast wrapper)
// ================================================================

/**
 * یک async action را در بر می‌گیرد.
 * @param {function} fn - async (args) => result
 * @param {object} options
 * @returns {function} wrapped
 */
export function useAction(fn, options = {}) {
  const { toast } = useBooksEngine();
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const activeRef = useRef(0);

  const run = useCallback(async (...args) => {
    const runId = ++activeRef.current;
    setState({ loading: true, error: null, data: null });
    try {
      const result = await fn(...args);
      if (runId !== activeRef.current) return result; // stale
      setState({ loading: false, error: null, data: result });
      if (result && result.ok === false && options.toastOnError !== false) {
        const msg = (result.errors || ['خطای نامشخص'])[0];
        toast?.(options.errorMessage || humanizeError(msg), 'error');
      } else if (result && result.ok === true && options.successMessage) {
        toast?.(options.successMessage, 'success');
      }
      return result;
    } catch (e) {
      if (runId !== activeRef.current) return { ok: false, errors: [String(e)] };
      setState({ loading: false, error: e, data: null });
      if (options.toastOnError !== false) {
        toast?.(options.errorMessage || humanizeError(e?.message || String(e)), 'error');
      }
      return { ok: false, errors: [e?.message || String(e)] };
    }
  }, [fn, options.toastOnError, options.successMessage, options.errorMessage, toast]);

  return { run, ...state };
}

// --- Workflow action hooks ---

export function useTeacherRequestActions() {
  const { user, toast } = useBooksEngine();

  const submit = useAction(
    useCallback(async (payload) => {
      return workflowEngine.submitTeacherRequest({ actor: user, ...payload });
    }, [user]),
    { successMessage: 'درخواست شما ثبت شد و برای بررسی ارسال شد.' }
  );

  const withdraw = useAction(
    useCallback(async (requestId) => {
      return workflowEngine.withdrawRequest({ actor: user, requestId });
    }, [user]),
    { successMessage: 'درخواست پس گرفته شد.' }
  );

  return { submit, withdraw };
}

export function useAdminRequestActions() {
  const { user, toast } = useBooksEngine();

  const approve = useAction(
    useCallback(async (requestId, note) => {
      return workflowEngine.approveRequest({ actor: user, requestId, adminNote: note });
    }, [user]),
    { successMessage: 'درخواست تایید شد.' }
  );

  const reject = useAction(
    useCallback(async (requestId, reason) => {
      return workflowEngine.rejectRequest({ actor: user, requestId, reason });
    }, [user]),
    { successMessage: 'درخواست رد شد.' }
  );

  const requestEdit = useAction(
    useCallback(async (requestId, reason) => {
      return workflowEngine.requestEditForRequest({ actor: user, requestId, reason });
    }, [user]),
    { successMessage: 'درخواست اصلاح ارسال شد.' }
  );

  const edit = useAction(
    useCallback(async (requestId, patch) => {
      return workflowEngine.adminEditRequest({ actor: user, requestId, patch });
    }, [user]),
    { successMessage: 'تغییرات ذخیره شد.' }
  );

  return { approve, reject, requestEdit, edit };
}

export function useVersionActions() {
  const { user, toast } = useBooksEngine();

  const submitForReview = useAction(
    useCallback(async (projectId, opts = {}) => {
      return versionManager.submitForReview(projectId, { actor: user, ...opts });
    }, [user]),
    { successMessage: 'نسخه برای بررسی ارسال شد.' }
  );

  const approveAndPublish = useAction(
    useCallback(async (projectId, note) => {
      return versionManager.approveAndPublish(projectId, { actor: user, note });
    }, [user]),
    { successMessage: 'نسخه منتشر شد.' }
  );

  const rejectPending = useAction(
    useCallback(async (projectId, reason) => {
      return versionManager.rejectPendingVersion(projectId, { actor: user, reason });
    }, [user]),
    { successMessage: 'نسخه رد شد.' }
  );

  const requestEdit = useAction(
    useCallback(async (projectId, reason) => {
      return versionManager.requestEditForPendingVersion(projectId, { actor: user, reason });
    }, [user]),
    { successMessage: 'درخواست اصلاح ارسال شد.' }
  );

  const rollbackTo = useAction(
    useCallback(async (projectId, targetVersionId, note) => {
      return rollbackManager.rollbackProjectToVersion({
        actor: user, projectId, targetVersionId, note,
      });
    }, [user]),
    { successMessage: 'نسخه بازگردانی شد.' }
  );

  return { submitForReview, approveAndPublish, rejectPending, requestEdit, rollbackTo };
}

export function useProjectActions() {
  const { user, toast } = useBooksEngine();

  const archive = useAction(
    useCallback(async (projectId) => {
      return dataEngine.project.softDelete(projectId);
    }, []),
    { successMessage: 'پروژه آرشیو شد.' }
  );

  const restore = useAction(
    useCallback(async (projectId) => {
      return dataEngine.project.restore(projectId);
    }, []),
    { successMessage: 'پروژه بازگردانی شد.' }
  );

  const suspend = useAction(
    useCallback(async (projectId) => {
      const res = dataEngine.project.transitionState(projectId, PROJECT_STATE.SUSPENDED, {
        byUserId: user?.id,
      });
      if (res.ok) {
        auditLog.record({
          actorId: user?.id, actorRole: user?.role,
          action: AUDIT_ACTION.PROJECT_SUSPEND,
          targetType: 'project', targetId: projectId,
        });
      }
      return res;
    }, [user]),
    { successMessage: 'پروژه معلق شد.' }
  );

  const resume = useAction(
    useCallback(async (projectId) => {
      const res = dataEngine.project.transitionState(projectId, PROJECT_STATE.PUBLISHED, {
        byUserId: user?.id,
      });
      if (res.ok) {
        auditLog.record({
          actorId: user?.id, actorRole: user?.role,
          action: AUDIT_ACTION.PROJECT_RESUME,
          targetType: 'project', targetId: projectId,
        });
      }
      return res;
    }, [user]),
    { successMessage: 'پروژه فعال شد.' }
  );



    const hardDelete = useAction(
    useCallback(async (projectId) => {
      // 🆕 ۰. اطلاعات پروژه را قبل از حذف نگه دار (برای cleanup سرور)
      const projectBefore = dataEngine.project.get(projectId);
      if (!projectBefore) return { ok: false, errors: ['not_found'] };

      // ۱. حذف کامل در data engine (این خودش entity:deleted برای project+versions emit می‌کند)
      const res = dataEngine.project.hardDelete(projectId);
      if (!res.ok) return res;

      // ۲. پاک‌سازی blobها (بعد از حذف، orphan می‌شوند و GC بعدی پاکشان می‌کند)
      if (res.blobIds && res.blobIds.length > 0) {
        for (const blobId of res.blobIds) {
          try {
            await engine.blobs.removeRef(blobId, 'project', projectId);
          } catch (e) {
            // بی‌خیال — GC دوره‌ای پاکش می‌کند
          }
        }
      }

      // ۳. ثبت در audit log
      auditLog.record({
        actorId: user?.id,
        actorRole: user?.role,
        action: AUDIT_ACTION.PROJECT_DELETE,
        targetType: 'project',
        targetId: projectId,
        before: {
          title: res.data.title,
          state: res.data.state,
          grade: res.data.grade,
          subject: res.data.subject,
          folderName: res.data.folderName,
        },
      });

      // ۴. broadcast برای هم‌زمان‌سازی تب‌های دیگر
      engine.broadcast('project:deleted', { projectId });

      try {
        engine.emit('project:hard-deleted', { projectId });
      } catch (e) { /* ignore */ }

      // 🆕 ۵. پاک‌سازی سمت سرور — همه‌ی داده‌های همه‌ی کاربران
      // (این مرحله در پس‌زمینه اجرا می‌شود؛ اگر fail شود، sync queue بعداً retry می‌کند)
      // 🆕 ۵. پاک‌سازی سمت سرور — همه‌ی داده‌های همه‌ی کاربران
      try {
        const { API_BASE } = await import('../core/books/apiClient.js');
        const base = API_BASE || '';
        const url = `${base}/__cleanup-project`;
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            projectId,
            grade: projectBefore.grade,
            subject: projectBefore.subject,
            folderName: projectBefore.folderName,
          }),
        }).catch((e) => {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('[hardDelete] server cleanup endpoint not available:', e.message);
          }
        });
      } catch (e) {
        // نادیده بگیر
      }

      // 🆕 ۶. پاک‌سازی فایل‌های آپلودشده از سرور ۳۰۰۲
      //    همه‌ی تصاویر و ویدیوهای این پروژه زیر
      //    media/exams/{teacherId}/{projectId}/ ذخیره شده‌اند
      try {
        const { FILE_API } = await import('../core/books/apiClient.js');
        const teacherIdForFiles = projectBefore.teacherId;
        if (teacherIdForFiles) {
          fetch(`${FILE_API}/api/delete-exam`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              teacherId: teacherIdForFiles,
              examId: projectId,
            }),
          })
            .then((r) => r.json())
            .then((d) => {
              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.info('[hardDelete] files cleanup result:', d);
              }
            })
            .catch((e) => {
              if (process.env.NODE_ENV !== 'production') {
                console.warn('[hardDelete] file cleanup failed:', e.message);
              }
            });
        }
      } catch (e) {
        // نادیده بگیر — فایل‌ها orphan می‌شوند (سرور ۳۰۰۲ فایل GC دوره‌ای ندارد،
        // ولی این خطا نباید UX حذف پروژه را بشکند)
      }

      return res;
    }, [user]),
    { successMessage: 'پروژه و تمام محتوای آن حذف شد.' }
  );








    return { archive, restore, suspend, resume, hardDelete };
}

// ================================================================
// SECTION 6: DRAFT EDITOR (Crown Jewel React side)
// ================================================================

/**
 * هوک جامع برای ویرایش draft یک پروژه.
 * شامل:
 *   - بارگذاری draft (یا ساخت از published)
 *   - auto-save با debounce
 *   - undo/redo
 *   - change indicator
 *   - عملیات chapter/lesson/step
 */
export function useDraftEditor(projectId) {
  const { user, toast } = useBooksEngine();
  const [state, setState] = useState({
    loading: true,
    error: null,
    draft: null,
    project: null,
    hasUnsaved: false,
    lastSavedAt: null,
    isSaving: false,
  });

  // undo/redo refs
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [historyTick, setHistoryTick] = useState(0);

  // --- بارگذاری اولیه ---
  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setState((s) => ({ ...s, loading: false, error: 'no_project_id' }));
      return;
    }

    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const project = dataEngine.project.get(projectId);
      if (!project) {
        setState((s) => ({ ...s, loading: false, error: 'project_not_found' }));
        return;
      }

      // قفل ویرایش در حال pending
      if (project.state === PROJECT_STATE.UPDATE_PENDING) {
        setState({
          loading: false,
          error: 'locked_pending_review',
          project,
          draft: project.pendingVersionId
            ? dataEngine.version.get(project.pendingVersionId)
            : null,
          hasUnsaved: false,
          lastSavedAt: null,
          isSaving: false,
        });
        return;
      }

      // draft موجود؟
      let draft = project.draftVersionId
        ? dataEngine.version.get(project.draftVersionId)
        : null;

      if (!draft) {
        // ساخت draft جدید
        const res = project.publishedVersionId
          ? versionManager.createDraftFromPublished(projectId, { actor: user })
          : versionManager.createInitialDraft(projectId, { actor: user });
        if (!res.ok) {
          setState((s) => ({ ...s, loading: false, error: res.errors?.[0] || 'draft_create_failed' }));
          return;
        }
        draft = res.data;
      }

      if (cancelled) return;
      undoStackRef.current = [];
      redoStackRef.current = [];
      setState({
        loading: false,
        error: null,
        draft,
        project,
        hasUnsaved: false,
        lastSavedAt: null,
        isSaving: false,
      });
    } catch (e) {
      if (!cancelled) {
        setState((s) => ({ ...s, loading: false, error: e.message || String(e) }));
      }
    }

    return () => { cancelled = true; };
  }, [projectId, user]);

  // --- auto-save ---
  const saveTimerRef = useRef(null);
  const saveDraft = useCallback(async (chapters, { silent = true } = {}) => {
    if (!projectId) return;
    const current = dataEngine.project.get(projectId);
    if (!current?.draftVersionId) return;

    setState((s) => ({ ...s, isSaving: true }));
    const res = dataEngine.version.updateChapters(current.draftVersionId, chapters);
    if (res.ok) {
      setState((s) => ({
        ...s,
        draft: res.data,
        isSaving: false,
        hasUnsaved: false,
        lastSavedAt: Date.now(),
      }));
      if (!silent) toast?.('ذخیره شد', 'success');
    } else {
      setState((s) => ({ ...s, isSaving: false }));
      if (!silent) toast?.('خطا در ذخیره', 'error');
    }
    return res;
  }, [projectId, toast]);

  // --- تغییر chapters با auto-save + push به undo ---
  const commitChapters = useCallback((newChapters, { pushHistory = true } = {}) => {
    setState((s) => {
      if (!s.draft) return s;
      if (pushHistory) {
        undoStackRef.current.push(JSON.parse(JSON.stringify(s.draft.chapters)));
        if (undoStackRef.current.length > 20) undoStackRef.current.shift();
        redoStackRef.current = [];
      }
      const updatedDraft = { ...s.draft, chapters: newChapters };
      // schedule auto-save
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveDraft(newChapters, { silent: true });
      }, 600);
      return { ...s, draft: updatedDraft, hasUnsaved: true };
    });
    setHistoryTick((t) => t + 1);
    // 🆕 برگرداندن chapters جدید تا مصرف‌کننده بتواند فوراً از آن استفاده کند
    return newChapters;
  }, [saveDraft]);

  // --- undo/redo ---
  const undo = useCallback(() => {
    setState((s) => {
      if (!s.draft || undoStackRef.current.length === 0) return s;
      const prev = undoStackRef.current.pop();
      redoStackRef.current.push(JSON.parse(JSON.stringify(s.draft.chapters)));
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveDraft(prev, { silent: true }), 600);
      return { ...s, draft: { ...s.draft, chapters: prev }, hasUnsaved: true };
    });
    setHistoryTick((t) => t + 1);
  }, [saveDraft]);

  const redo = useCallback(() => {
    setState((s) => {
      if (!s.draft || redoStackRef.current.length === 0) return s;
      const next = redoStackRef.current.pop();
      undoStackRef.current.push(JSON.parse(JSON.stringify(s.draft.chapters)));
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveDraft(next, { silent: true }), 600);
      return { ...s, draft: { ...s.draft, chapters: next }, hasUnsaved: true };
    });
    setHistoryTick((t) => t + 1);
  }, [saveDraft]);

  // --- عملیات فصل ---
  const addChapter = useCallback((title) => {
    if (!state.draft) return;
    const newChapter = {
      _id: dataEngine.utils.genChapterId(),
      title: title || 'فصل جدید',
      description: '',
      order: state.draft.chapters.length,
      lessons: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    commitChapters([...state.draft.chapters, newChapter]);
  }, [state.draft, commitChapters]);

  const updateChapter = useCallback((chapterId, patch) => {
    if (!state.draft) return;
    const next = state.draft.chapters.map((ch) =>
      ch._id === chapterId ? { ...ch, ...patch, updatedAt: Date.now() } : ch
    );
    commitChapters(next);
  }, [state.draft, commitChapters]);

  const removeChapter = useCallback((chapterId) => {
    if (!state.draft) return;
    const next = state.draft.chapters.filter((ch) => ch._id !== chapterId);
    commitChapters(next);
  }, [state.draft, commitChapters]);

  const moveChapter = useCallback((chapterId, direction) => {
    if (!state.draft) return;
    const list = [...state.draft.chapters];
    const idx = list.findIndex((c) => c._id === chapterId);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    commitChapters(dataEngine.utils.resequence(list));
  }, [state.draft, commitChapters]);

  const reorderChapters = useCallback((fromIndex, toIndex) => {
    if (!state.draft) return;
    const list = [...state.draft.chapters];
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) return;
    const [item] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, item);
    commitChapters(dataEngine.utils.resequence(list));
  }, [state.draft, commitChapters]);

  // --- عملیات درس ---
  const addLesson = useCallback((chapterId, title) => {
  if (!state.draft) return;
  const next = state.draft.chapters.map((ch) => {
    if (ch._id !== chapterId) return ch;

    // ✅ محاسبه‌ی شماره‌ی درس یکتا در کل کتاب
    const allLessons = state.draft.chapters.flatMap((c) => c.lessons || []);
    const maxNumber = allLessons.reduce((max, ls) => {
      const match = String(ls.legacyLessonId || '').match(/^Lesson(\d+)$/);
      const num = match ? parseInt(match[1], 10) : 0;
      return Math.max(max, num);
    }, 0);

    const newLesson = {
      _id: dataEngine.utils.genLessonId(),
      legacyLessonId: `Lesson${maxNumber + 1}`,  // ✅ جدید
      title: title || 'درس جدید',
      description: '',
      imageBlobId: null,
      order: ch.lessons.length,
      steps: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    return { ...ch, lessons: [...ch.lessons, newLesson], updatedAt: Date.now() };
  });
  commitChapters(next);
}, [state.draft, commitChapters]);














  const updateLesson = useCallback((chapterId, lessonId, patch) => {
    if (!state.draft) return;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) =>
          ls._id === lessonId ? { ...ls, ...patch, updatedAt: Date.now() } : ls
        ),
        updatedAt: Date.now(),
      };
    });
    commitChapters(next);
  }, [state.draft, commitChapters]);

  const removeLesson = useCallback((chapterId, lessonId) => {
    if (!state.draft) return;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return { ...ch, lessons: ch.lessons.filter((ls) => ls._id !== lessonId) };
    });
    commitChapters(next);
  }, [state.draft, commitChapters]);

  const moveLesson = useCallback((chapterId, lessonId, direction) => {
    if (!state.draft) return;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      const list = [...ch.lessons];
      const idx = list.findIndex((l) => l._id === lessonId);
      if (idx < 0) return ch;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= list.length) return ch;
      [list[idx], list[target]] = [list[target], list[idx]];
      return { ...ch, lessons: dataEngine.utils.resequence(list), updatedAt: Date.now() };
    });
    commitChapters(next);
  }, [state.draft, commitChapters]);

  const moveLessonToChapter = useCallback((fromChapterId, toChapterId, lessonId, toIndex = null) => {
    if (!state.draft) return;
    let moving = null;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== fromChapterId) return ch;
      const found = ch.lessons.find((l) => l._id === lessonId);
      if (found) moving = found;
      return { ...ch, lessons: ch.lessons.filter((l) => l._id !== lessonId) };
    });
    if (!moving) return;
    const final = next.map((ch) => {
      if (ch._id !== toChapterId) return ch;
      const list = [...ch.lessons];
      const insertAt = toIndex == null ? list.length : Math.min(toIndex, list.length);
      list.splice(insertAt, 0, moving);
      return { ...ch, lessons: dataEngine.utils.resequence(list), updatedAt: Date.now() };
    });
    commitChapters(final);
  }, [state.draft, commitChapters]);

  // --- عملیات مرحله ---
  const addStep = useCallback((chapterId, lessonId, title, options = {}) => {
    if (!state.draft) return null;
    const {
      type = 'lesson',
      returnId = false,
      quizId = null,
      quizMeta = null,
    } = options;

    const newStepId = dataEngine.utils.genStepId();

    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          const newStep = {
            _id: newStepId,
            type,
            quizId,
            quizMeta,
            title: title || (type === 'quiz' ? 'آزمون جدید' : 'مرحله جدید'),
            description: '',
            durationMinutes: 0,
            order: ls.steps.length,
            videos: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };
          return { ...ls, steps: [...ls.steps, newStep], updatedAt: Date.now() };
        }),
      };
    });
    commitChapters(next);
    return returnId ? newStepId : null;
  }, [state.draft, commitChapters]);

  const updateStep = useCallback((chapterId, lessonId, stepId, patch) => {
    if (!state.draft) return null;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          return {
            ...ls,
            steps: ls.steps.map((st) =>
              st._id === stepId ? { ...st, ...patch, updatedAt: Date.now() } : st
            ),
            updatedAt: Date.now(),
          };
        }),
      };
    });
    return commitChapters(next);   // 🆕 برگرداندن chapters جدید
  }, [state.draft, commitChapters]);

  const removeStep = useCallback((chapterId, lessonId, stepId) => {
    if (!state.draft) return;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          return { ...ls, steps: ls.steps.filter((s) => s._id !== stepId) };
        }),
      };
    });
    commitChapters(next);
  }, [state.draft, commitChapters]);

  const moveStep = useCallback((chapterId, lessonId, stepId, direction) => {
    if (!state.draft) return;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          const list = [...ls.steps];
          const idx = list.findIndex((s) => s._id === stepId);
          if (idx < 0) return ls;
          const target = direction === 'up' ? idx - 1 : idx + 1;
          if (target < 0 || target >= list.length) return ls;
          [list[idx], list[target]] = [list[target], list[idx]];
          return { ...ls, steps: dataEngine.utils.resequence(list) };
        }),
      };
    });
    commitChapters(next);
  }, [state.draft, commitChapters]);

  // --- عملیات ویدیو ---
  const addVideo = useCallback((chapterId, lessonId, stepId, videoRef) => {
    if (!state.draft) return;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          return {
            ...ls,
            steps: ls.steps.map((st) => {
              if (st._id !== stepId) return st;
              const newVideo = {
                _id: dataEngine.utils.genVideoId(),
                blobId: videoRef.blobId || null,
                externalUrl: videoRef.externalUrl || null,
                title: videoRef.title || '',
                mimeType: videoRef.mimeType || '',
                size: videoRef.size || 0,
                durationSeconds: videoRef.durationSeconds || 0,
                order: st.videos.length,
                createdAt: Date.now(),
              };
              return { ...st, videos: [...st.videos, newVideo], updatedAt: Date.now() };
            }),
          };
        }),
      };
    });
    commitChapters(next);
  }, [state.draft, commitChapters]);

  const removeVideo = useCallback((chapterId, lessonId, stepId, videoId) => {
    if (!state.draft) return;
    // پیدا کردن blobId برای پاک‌سازی refcount
    let removedBlobId = null;
    const next = state.draft.chapters.map((ch) => {
      if (ch._id !== chapterId) return ch;
      return {
        ...ch,
        lessons: ch.lessons.map((ls) => {
          if (ls._id !== lessonId) return ls;
          return {
            ...ls,
            steps: ls.steps.map((st) => {
              if (st._id !== stepId) return st;
              const v = st.videos.find((x) => x._id === videoId);
              if (v?.blobId) removedBlobId = v.blobId;
              return { ...st, videos: st.videos.filter((x) => x._id !== videoId) };
            }),
          };
        }),
      };
    });
    commitChapters(next);
    // کاهش refCount
    if (removedBlobId) {
      engine.blobs.removeRef(removedBlobId, 'video', videoId).catch(() => {});
    }
  }, [state.draft, commitChapters]);

  // --- force save ---
  // 🆕 حالا می‌تواند chaptersOverride بگیرد تا از state کهنه استفاده نکند
  const forceSave = useCallback(async (chaptersOverride = null) => {
    const chapters = chaptersOverride || state.draft?.chapters;
    if (!chapters) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    return saveDraft(chapters, { silent: false });
  }, [state.draft, saveDraft]);

  // --- cleanup ---
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const canUndo = undoStackRef.current.length > 0;
  const canRedo = redoStackRef.current.length > 0;

  return {
    // state
    loading: state.loading,
    error: state.error,
    draft: state.draft,
    project: state.project,
    hasUnsaved: state.hasUnsaved,
    lastSavedAt: state.lastSavedAt,
    isSaving: state.isSaving,
    canUndo,
    canRedo,
    // actions
    addChapter, updateChapter, removeChapter, moveChapter, reorderChapters,
    addLesson, updateLesson, removeLesson, moveLesson, moveLessonToChapter,
    addStep, updateStep, removeStep, moveStep,
    addVideo, removeVideo,
    undo, redo, forceSave,
    _internal: { commitChapters, saveDraft },
  };
}














// ================================================================
// 🆕 useFreeStudyEditor — ویرایشگر مطالعه آزاد
// ================================================================
export function useFreeStudyEditor(projectId) {
  const { user, toast } = useBooksEngine();
  const [state, setState] = useState({
    loading: true,
    error: null,
    draft: null,
    project: null,
    hasUnsaved: false,
    lastSavedAt: null,
    isSaving: false,
  });

  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const saveTimerRef = useRef(null);
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [, setHistoryTick] = useState(0);

  // ─── بارگذاری اولیه ───
  useEffect(() => {
    if (!projectId) {
      setState((s) => ({ ...s, loading: false, error: 'no_project_id' }));
      return;
    }
    try {
      const project = dataEngine.project.get(projectId);
      if (!project) {
        setState((s) => ({ ...s, loading: false, error: 'project_not_found' }));
        return;
      }
      if (project.type !== 'freeStudy') {
        setState((s) => ({ ...s, loading: false, error: 'not_free_study' }));
        return;
      }
      if (project.state === PROJECT_STATE.UPDATE_PENDING) {
        setState({
          loading: false,
          error: 'locked_pending_review',
          project,
          draft: project.pendingVersionId
            ? dataEngine.version.get(project.pendingVersionId)
            : null,
          hasUnsaved: false,
          lastSavedAt: null,
          isSaving: false,
        });
        return;
      }

      let draft = project.draftVersionId
        ? dataEngine.version.get(project.draftVersionId)
        : null;

      if (!draft) {
        const res = project.publishedVersionId
          ? versionManager.createDraftFromPublished(projectId, { actor: user })
          : versionManager.createInitialDraft(projectId, { actor: user });
        if (!res.ok) {
          setState((s) => ({
            ...s,
            loading: false,
            error: res.errors?.[0] || 'draft_create_failed',
          }));
          return;
        }
        draft = res.data;
      }

      undoStackRef.current = [];
      redoStackRef.current = [];
      setState({
        loading: false,
        error: null,
        draft,
        project,
        hasUnsaved: false,
        lastSavedAt: null,
        isSaving: false,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e.message || String(e) }));
    }
  }, [projectId, user?.id]); // 🆕 user?.id (نه user object)

  // ─── ذخیره ───
  const saveDraft = useCallback(async (videos, { silent = true } = {}) => {
    if (!projectId) return;
    const current = dataEngine.project.get(projectId);
    if (!current?.draftVersionId) return;

    setState((s) => ({ ...s, isSaving: true }));
    const res = dataEngine.version.updateVideos(current.draftVersionId, videos);
    if (res.ok) {
      setState((s) => ({
        ...s,
        draft: res.data,
        isSaving: false,
        hasUnsaved: false,
        lastSavedAt: Date.now(),
      }));
      if (!silent) toast?.('ذخیره شد', 'success');
    } else {
      setState((s) => ({ ...s, isSaving: false }));
      if (!silent) toast?.('خطا در ذخیره', 'error');
    }
    return res;
  }, [projectId, toast]);

  // ─── تغییر اتمیک روی videos (functional setState) ───
  const applyVideosChange = useCallback((mutator, { pushHistory = true } = {}) => {
    setState((s) => {
      if (!s.draft) return s;
      const currentVideos = s.draft.videos || [];
      const nextVideos = mutator(currentVideos);
      if (nextVideos === currentVideos) return s;

      if (pushHistory) {
        undoStackRef.current.push(JSON.parse(JSON.stringify(currentVideos)));
        if (undoStackRef.current.length > 20) undoStackRef.current.shift();
        redoStackRef.current = [];
      }
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveDraft(nextVideos), 600);
      return { ...s, draft: { ...s.draft, videos: nextVideos }, hasUnsaved: true };
    });
    setHistoryTick((t) => t + 1);
  }, [saveDraft]);

  const addVideo = useCallback((videoData) => {
    applyVideosChange((currentVideos) => {
      const newVideo = dataEngine.factories.createFreeStudyVideo({
        ...videoData,
        order: currentVideos.length,
      });
      return [...currentVideos, newVideo];
    });
  }, [applyVideosChange]);

  const updateVideo = useCallback((videoId, patch) => {
    applyVideosChange((currentVideos) =>
      currentVideos.map((v) =>
        v._id === videoId ? { ...v, ...patch, updatedAt: Date.now() } : v
      )
    );
  }, [applyVideosChange]);

  const removeVideo = useCallback((videoId) => {
    applyVideosChange((currentVideos) => {
      const next = currentVideos.filter((v) => v._id !== videoId);
      return dataEngine.utils.resequence(next);
    });
  }, [applyVideosChange]);

  const moveVideo = useCallback((videoId, direction) => {
    applyVideosChange((currentVideos) => {
      const list = [...currentVideos];
      const idx = list.findIndex((v) => v._id === videoId);
      if (idx < 0) return currentVideos;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= list.length) return currentVideos;
      [list[idx], list[target]] = [list[target], list[idx]];
      return dataEngine.utils.resequence(list);
    });
  }, [applyVideosChange]);

  const undo = useCallback(() => {
    setState((s) => {
      if (!s.draft || undoStackRef.current.length === 0) return s;
      const prev = undoStackRef.current.pop();
      redoStackRef.current.push(JSON.parse(JSON.stringify(s.draft.videos || [])));
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveDraft(prev), 600);
      return { ...s, draft: { ...s.draft, videos: prev }, hasUnsaved: true };
    });
    setHistoryTick((t) => t + 1);
  }, [saveDraft]);

  const redo = useCallback(() => {
    setState((s) => {
      if (!s.draft || redoStackRef.current.length === 0) return s;
      const next = redoStackRef.current.pop();
      undoStackRef.current.push(JSON.parse(JSON.stringify(s.draft.videos || [])));
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveDraft(next), 600);
      return { ...s, draft: { ...s.draft, videos: next }, hasUnsaved: true };
    });
    setHistoryTick((t) => t + 1);
  }, [saveDraft]);

  // 🆕 با stateRef (رفع state کهنه)
  const forceSave = useCallback(async (videosOverride = null) => {
    const videos = videosOverride || stateRef.current.draft?.videos;
    if (!videos) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    return saveDraft(videos, { silent: false });
  }, [saveDraft]);

  const updateProjectMeta = useCallback((patch) => {
    if (!projectId) return { ok: false, errors: ['no_project_id'] };
    const res = dataEngine.project.update(projectId, patch);
    if (res.ok) {
      setState((s) => ({ ...s, project: res.data }));
    } else {
      // 🆕 در حین تایپ، خطاهای جزئی را ignore کن
      const errs = res.errors || [];
      const benign = new Set([
        'title_required', 'folder_name_required', 'subject_required', 'grade_required',
      ]);
      const isBenign = errs.length > 0 && errs.every((e) => benign.has(e));
      if (!isBenign) {
        console.warn('[updateProjectMeta] failed:', errs);
      }
    }
    return res;
  }, [projectId]);

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  return {
    ...state,
    canUndo: undoStackRef.current.length > 0,
    canRedo: redoStackRef.current.length > 0,
    addVideo,
    updateVideo,
    removeVideo,
    moveVideo,
    undo,
    redo,
    forceSave,
    updateProjectMeta,
  };
}

// ================================================================
// 🆕 Free Study hooks
// ================================================================

export function useMyFreeStudyProjects() {
  const { user } = useBooksEngine();
  const tick = useEngineTick('registry:project:changed');
  return useMemo(() => {
    if (!user?.id) return EMPTY_ARRAY;
    const tp = dataEngine.teacher.getByUserId(user.id);
    if (!tp) return EMPTY_ARRAY;
    return dataEngine.project.listByTeacher(tp.id, { type: 'freeStudy' });
  }, [user?.id, tick]);
}

export function usePendingFreeStudyProjects() {
  const tick = useEngineTick('registry:project:changed');
  return useMemo(() => {
    return dataEngine.project.list({
      type: 'freeStudy',
      state: [PROJECT_STATE.PENDING_INITIAL, PROJECT_STATE.UPDATE_PENDING],
    });
  }, [tick]);
}

export function useAllFreeStudyProjects() {
  const tick = useEngineTick('registry:project:changed');
  return useMemo(() => {
    return dataEngine.project.list({ type: 'freeStudy' });
  }, [tick]);
}











/** auto-save عمومی برای هر value */
export function useAutoSave(value, onSave, { delay = 500, enabled = true } = {}) {
  const [state, setState] = useState({ isSaving: false, lastSavedAt: null, hasUnsaved: false });
  const savedRef = useRef(value);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (value === savedRef.current) {
      if (state.hasUnsaved) setState((s) => ({ ...s, hasUnsaved: false }));
      return;
    }
    setState((s) => ({ ...s, hasUnsaved: true }));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setState((s) => ({ ...s, isSaving: true }));
      try {
        await onSave(value);
        savedRef.current = value;
        setState({ isSaving: false, lastSavedAt: Date.now(), hasUnsaved: false });
      } catch (e) {
        setState((s) => ({ ...s, isSaving: false }));
      }
    }, delay);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, enabled, delay, onSave, state.hasUnsaved]);

  const flush = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (value === savedRef.current) return;
    setState((s) => ({ ...s, isSaving: true }));
    try {
      await onSave(value);
      savedRef.current = value;
      setState({ isSaving: false, lastSavedAt: Date.now(), hasUnsaved: false });
    } catch {
      setState((s) => ({ ...s, isSaving: false }));
    }
  }, [value, onSave]);

  return { ...state, flush };
}

/** undo/redo عمومی */
export function useUndoRedo(initialState, { max = 20 } = {}) {
  const [state, setState] = useState(initialState);
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const [, setTick] = useState(0);

  const commit = useCallback((next, { silent = false } = {}) => {
    if (!silent) {
      undoRef.current.push(JSON.parse(JSON.stringify(state)));
      if (undoRef.current.length > max) undoRef.current.shift();
      redoRef.current = [];
    }
    setState(next);
    setTick((t) => t + 1);
  }, [state, max]);

  const undo = useCallback(() => {
    if (undoRef.current.length === 0) return;
    const prev = undoRef.current.pop();
    redoRef.current.push(JSON.parse(JSON.stringify(state)));
    setState(prev);
    setTick((t) => t + 1);
  }, [state]);

  const redo = useCallback(() => {
    if (redoRef.current.length === 0) return;
    const next = redoRef.current.pop();
    undoRef.current.push(JSON.parse(JSON.stringify(state)));
    setState(next);
    setTick((t) => t + 1);
  }, [state]);

  const reset = useCallback((newState) => {
    undoRef.current = [];
    redoRef.current = [];
    setState(newState);
    setTick((t) => t + 1);
  }, []);

  return {
    state,
    commit,
    undo,
    redo,
    reset,
    canUndo: undoRef.current.length > 0,
    canRedo: redoRef.current.length > 0,
    size: { undo: undoRef.current.length, redo: redoRef.current.length },
  };
}

/** keyboard shortcuts */
export function useKeyboardShortcuts(map, { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      const isMod = e.ctrlKey || e.metaKey;
      for (const [combo, fn] of Object.entries(map)) {
        const parts = combo.toLowerCase().split('+');
        const needsCtrl = parts.includes('ctrl') || parts.includes('mod');
        const needsShift = parts.includes('shift');
        const key = parts[parts.length - 1];
        const matchKey = e.key.toLowerCase() === key;
        if (
          matchKey &&
          ((needsCtrl && isMod) || (!needsCtrl && !isMod)) &&
          (needsShift ? e.shiftKey : true)
        ) {
          e.preventDefault();
          try { fn(e); } catch (err) { console.warn('shortcut error:', err); }
          return;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [map, enabled]);
}

/** هشدار قبل از بستن صفحه */
export function useBeforeUnload(shouldWarn, message = 'تغییرات ذخیره‌نشده دارید.') {
  useEffect(() => {
    if (!shouldWarn) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldWarn, message]);
}






// ================================================================
// 🆕 SECTION 6.5: STEP QUIZ HOOKS
// ================================================================

/** گرفتن آزمون linked به یک step */
export function useStepQuiz(versionId, stepId) {
  const examCtx = useExamsOptional();
  const tick = useEngineTick('registry:version:changed');
  return useMemo(() => {
    if (!examCtx || !versionId || !stepId) return null;
    return examCtx.getStepQuiz({ versionId, stepId });
  }, [examCtx, versionId, stepId, tick, examCtx?.exams]);
}

/** وضعیت قبولی دانش‌آموز در یک مرحله */
export function useStepQuizPass(userId, projectId, stepId) {
  const tick = useEngineTick(['quiz:step-passed', 'registry:version:changed']);
  return useMemo(() => {
    if (!userId || !projectId || !stepId) return null;
    try {
      return progressManager.getQuizStepPass(userId, projectId, stepId);
    } catch { return null; }
  }, [userId, projectId, stepId, tick]);
}




/** 🆕 وضعیت قفل درس‌ها و فصل‌های یک کتاب */
export function useBookLocks(userId, projectId, grade, subject, folderName, chapters) {
  const tick = useEngineTick([
    'progress:changed',
    'quiz:step-progress-changed',
    'quiz:step-passed',
  ]);
  return useMemo(() => {
    if (!userId || userId === 'unknown' || !projectId || !chapters?.length) {
      return { chapterComplete: [], lessonComplete: [] };
    }
    try {
      return progressManager.getBookLocks(
        userId, projectId, grade, subject, folderName, chapters
      );
    } catch {
      return { chapterComplete: [], lessonComplete: [] };
    }
  }, [userId, projectId, grade, subject, folderName, chapters, tick]);
}







/** access state (attempts + unlock) */
export function useStepQuizAccess(userId, quizId) {
  const examCtx = useExamsOptional();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const unsub1 = engine.on('quiz:unlocked', () => setTick(t => t + 1));
    const unsub2 = engine.on('quiz:step-passed', () => setTick(t => t + 1));
    return () => { try { unsub1(); } catch {} try { unsub2(); } catch {} };
  }, []);
  return useMemo(() => {
    if (!examCtx || !userId || !quizId) {
      return { attempts: [], unlockedWithPayment: [], passed: false };
    }
    return examCtx.getStepQuizAccess(userId, quizId);
  }, [examCtx, userId, quizId, tick, examCtx?.exams]);
}

/** canAttempt check */
export function useStepQuizCanAttempt(userId, quizId) {
  const examCtx = useExamsOptional();
  const tick = useEngineTick(['quiz:unlocked', 'quiz:step-passed']);
  return useMemo(() => {
    if (!examCtx || !userId || !quizId) {
      return { allowed: false, reason: 'not-ready' };
    }
    return examCtx.canAttemptStepQuiz(userId, quizId);
  }, [examCtx, userId, quizId, tick, examCtx?.exams]);
}



/** 🆕 خواندن progress یک step (منبع واحد) */
export function useQuizStepProgress(userId, projectId, stepId) {
  const tick = useEngineTick(['quiz:step-progress-changed', 'quiz:unlocked']);
  return useMemo(() => {
    if (!userId || !projectId || !stepId) return null;
    try {
      return progressManager.getQuizStepProgress(userId, projectId, stepId);
    } catch { return null; }
  }, [userId, projectId, stepId, tick]);
}

/** 🆕 خواندن progress همه‌ی مراحل آزمون یک درس */
export function useQuizProgressForLesson(userId, projectId, lesson) {
  const tick = useEngineTick(['quiz:step-progress-changed', 'quiz:unlocked']);
  return useMemo(() => {
    if (!userId || !projectId || !lesson?.steps) return {};
    const all = progressManager.getAllQuizProgressForProject(userId, projectId);
    const out = {};
    for (const st of lesson.steps) {
      if (st.type === 'quiz' && st._id) {
        out[st._id] = all[st._id] || null;
      }
    }
    return out;
  }, [userId, projectId, lesson, tick]);
}

/** 🆕 بررسی مجاز بودن شرکت */
export function useCanAttemptQuiz(userId, projectId, stepId, opts = {}) {
  const tick = useEngineTick(['quiz:step-progress-changed', 'quiz:unlocked']);
  const key = JSON.stringify(opts);
  return useMemo(() => {
    if (!userId || !projectId || !stepId) {
      return { allowed: false, reason: 'not-ready' };
    }
    try {
      return progressManager.canAttemptQuiz(userId, projectId, stepId, opts);
    } catch {
      return { allowed: false, reason: 'error' };
    }
  }, [userId, projectId, stepId, key, tick]);
}







// ================================================================
// SECTION 6.6: SUBSCRIPTION HOOKS 🆕
// ================================================================

/** خواندن وضعیت اشتراک پروژه برای کاربر */
export function useProjectSubscription(userId, projectId) {
  const tick = useEngineTick('subscription:changed');
  return useMemo(() => {
    if (!userId || !projectId) {
      return { status: 'none', daysLeft: 0, expiresAt: null, record: null };
    }
    try {
      return subscriptionManager.getStatus(userId, projectId);
    } catch (e) {
      return { status: 'none', daysLeft: 0, expiresAt: null, record: null };
    }
  }, [userId, projectId, tick]);
}

/** چک دسترسی به یک درس (compute-heavy → memoize) */
export function useStepAccess({ user, project, lesson, userId }) {
  const tick = useEngineTick(['subscription:changed', 'registry:project:changed']);
  return useMemo(() => {
    if (!project) return { allowed: true, reason: 'no-project' };
    try {
      return subscriptionManager.checkAccess({ user, project, lesson, userId });
    } catch (e) {
      return { allowed: false, reason: 'error' };
    }
  }, [user, project, lesson, userId, tick]);
}

/** action خرید اشتراک */
export function useSubscribeToProject() {
  const { user } = useBooksEngine();
  return useAction(
    useCallback(async ({ projectId, teacherId }) => {
      if (!user?.id) return { ok: false, errors: ['not-logged-in'] };
      if (!projectId) return { ok: false, errors: ['no-project-id'] };

      try {
        await startPayment({
          userId: user.id,
          purpose: PAYMENT_PURPOSE.LESSON_SUBSCRIPTION,
          metadata: {
            projectId,
            teacherId: teacherId || null,
          },
          description: 'اشتراک کتاب',
        });
        return { ok: true };
      } catch (err) {
        return { ok: false, errors: [err?.message || 'payment_failed'] };
      }
    }, [user]),
    { successMessage: 'در حال انتقال به درگاه...' }
  );
}

/** لیست مشترکان پروژه (برای پنل معلم) */
export function useProjectSubscribers(projectId) {
  const tick = useEngineTick('subscription:changed');
  return useMemo(() => {
    if (!projectId) return [];
    try {
      return subscriptionManager.listByProject(projectId);
    } catch { return []; }
  }, [projectId, tick]);
}




// ================================================================
// SECTION 7: MEDIA HOOKS
// ================================================================

/** URL برای یک Blob با cleanup خودکار */
export function useBlobUrl(blobId) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(!!blobId);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!blobId) { setUrl(null); setLoading(false); setError(null); return; }
    setLoading(true);
    engine.blobs.getUrl(blobId)
      .then((u) => { if (!cancelled) { setUrl(u); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e); setLoading(false); } });
    return () => { cancelled = true; };
  }, [blobId]);

  return { url, loading, error };
}

/** متادیتای Blob */
export function useBlobMeta(blobId) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(!!blobId);

  useEffect(() => {
    let cancelled = false;
    if (!blobId) { setMeta(null); setLoading(false); return; }
    setLoading(true);
    engine.blobs.loadMeta(blobId)
      .then((m) => { if (!cancelled) { setMeta(m); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [blobId]);

  return { meta, loading };
}

/** آپلود ویدیو با chunked + progress */
export function useVideoUpload() {
  const { toast } = useBooksEngine();
  const [state, setState] = useState({ uploading: false, progress: 0, speed: 0, error: null });
  const activeRef = useRef(0);
  const lastLoadedRef = useRef(0);
  const lastTimeRef = useRef(0);

  const upload = useCallback(async (file, meta = {}) => {
    const runId = ++activeRef.current;
    lastLoadedRef.current = 0;
    lastTimeRef.current = Date.now();
    setState({ uploading: true, progress: 0, speed: 0, error: null });
    try {
      const result = await engine.blobs.uploadLarge(
        file,
        { refType: 'video', mimeType: file.type, filename: file.name, ...meta },
        (p) => {
          if (runId !== activeRef.current) return;

          // سازگاری با هر دو شکل (number یا object)
          const percent = typeof p === 'object' ? (p?.percent ?? 0) : (p || 0);
          const loaded = typeof p === 'object' ? (p?.loaded ?? 0) : 0;

          // محاسبه سرعت (فقط وقتی loaded > 0)
          let speed = 0;
          if (loaded > 0) {
            const now = Date.now();
            const elapsedSec = (now - lastTimeRef.current) / 1000;
            const bytesDelta = loaded - lastLoadedRef.current;
            if (elapsedSec >= 0.3 && bytesDelta > 0) {
              speed = bytesDelta / elapsedSec; // bytes/sec
              lastLoadedRef.current = loaded;
              lastTimeRef.current = now;
            }
          }

          setState((s) => ({
            ...s,
            progress: percent,
            speed: speed > 0 ? speed : s.speed,
          }));
        }
      );
      if (runId === activeRef.current) {
        setState({ uploading: false, progress: 100, speed: 0, error: null });
      }
      return result;
    } catch (e) {
      if (runId === activeRef.current) {
        setState({ uploading: false, progress: 0, speed: 0, error: e.message || String(e) });
      }
      toast?.('خطا در آپلود ویدیو', 'error');
      throw e;
    }
  }, [toast]);

  const cancel = useCallback(() => {
    activeRef.current++;
    setState({ uploading: false, progress: 0, speed: 0, error: null });
  }, []);

  return { ...state, upload, cancel };
}

/** آپلود تصویر کوچک */
export function useImageUpload() {
  const { toast } = useBooksEngine();
  const [state, setState] = useState({ uploading: false, error: null });

  const upload = useCallback(async (file, meta = {}) => {
    setState({ uploading: true, error: null });
    try {
      const result = await engine.blobs.save(file, {
        refType: 'image', mimeType: file.type, filename: file.name, ...meta,
      });
      setState({ uploading: false, error: null });
      return result;
    } catch (e) {
      setState({ uploading: false, error: e.message || String(e) });
      toast?.('خطا در آپلود تصویر', 'error');
      throw e;
    }
  }, [toast]);

  return { ...state, upload };
}

// ================================================================
// SECTION 8: NOTIFICATION HOOKS
// ================================================================

export function useNotifications({ limit = 30 } = {}) {
  const { user } = useBooksEngine();
  const [items, setItems] = useState(() =>
    user?.id ? notificationBus.listFor(user.id, { limit }) : EMPTY_ARRAY
  );

  const reload = useCallback(() => {
    if (!user?.id) { setItems(EMPTY_ARRAY); return; }
    setItems(notificationBus.listFor(user.id, { limit }));
  }, [user?.id, limit]);

  useEffect(() => {
    if (!user?.id) { setItems(EMPTY_ARRAY); return; }
    reload();
    const unsub = notificationBus.subscribe(user.id, () => reload());
    const unsubBroadcast = engine.on('sync:notif:new', reload);
    return () => { unsub(); unsubBroadcast(); };
  }, [user?.id, reload]);

  const markRead = useCallback((notifId) => {
    if (!user?.id) return;
    notificationBus.markRead(user.id, notifId);
    reload();
  }, [user?.id, reload]);

  const markAllRead = useCallback(() => {
    if (!user?.id) return;
    notificationBus.markRead(user.id);
    reload();
  }, [user?.id, reload]);

  const remove = useCallback((notifId) => {
    if (!user?.id) return;
    notificationBus.remove(user.id, notifId);
    reload();
  }, [user?.id, reload]);

  return { items, reload, markRead, markAllRead, remove };
}

export function useUnreadCount() {
  const { user } = useBooksEngine();
  const [count, setCount] = useState(() =>
    user?.id ? notificationBus.unreadCount(user.id) : 0
  );

  useEffect(() => {
    if (!user?.id) { setCount(0); return; }
    setCount(notificationBus.unreadCount(user.id));
    
    const updateCount = () => setCount(notificationBus.unreadCount(user.id));
    
    const unsub = notificationBus.subscribe(user.id, updateCount);
    const unsubBroadcast = engine.on('sync:notif:new', updateCount);
    const unsubRead = engine.on('sync:notif:read', updateCount);
    // 🆕 گوش دادن به رویداد حذف اعلان
    const unsubRemove = engine.on('sync:notif:removed', updateCount);
    
    return () => { 
      unsub(); 
      unsubBroadcast(); 
      unsubRead(); 
      unsubRemove(); // 🆕
    };
  }, [user?.id]);

  return count;
}

// ================================================================
// SECTION 9: SYSTEM HOOKS
// ================================================================

/** وضعیت localStorage + IndexedDB */
export function useStorageStatus() {
  const [status, setStatus] = useState(() => ({
    local: engine.quota.getLocalStatus(),
    idb: null,
  }));

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const local = engine.quota.getLocalStatus();
      const idb = await engine.quota.getIDBStatus();
      if (!cancelled) setStatus({ local, idb });
    };
    refresh();
    const unsub1 = engine.on('storage:write', refresh);
    const unsub2 = engine.on('blob:saved', refresh);
    const unsub3 = engine.on('blob:deleted', refresh);
    const unsub4 = engine.on('quota:cleanup-done', refresh);
    const interval = setInterval(refresh, 60000);
    return () => {
      cancelled = true;
      unsub1(); unsub2(); unsub3(); unsub4();
      clearInterval(interval);
    };
  }, []);

  return status;
}

/** گوش دادن به رویدادهای multi-tab */
export function useMultiTabSync(eventType, handler) {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; }, [handler]);
  useEffect(() => {
    const unsub = engine.on(`sync:${eventType}`, (payload) => {
      try { handlerRef.current?.(payload); } catch (e) { console.warn('multi-tab handler:', e); }
    });
    return unsub;
  }, [eventType]);
}

/** وضعیت آنلاین */
export function useOnlineStatus() {
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** اجرای periodic cleanup دستی */
export function useManualCleanup() {
  const [running, setRunning] = useState(false);
  const run = useCallback(async () => {
    setRunning(true);
    try {
      await engine.quota.periodicCleanup();
      await engine.blobs.garbageCollect();
    } finally {
      setRunning(false);
    }
  }, []);
  return { run, running };
}

// ================================================================
// SECTION 10: MIGRATION HOOKS
// ================================================================

export function useMigrationStatus() {
  const [status, setStatus] = useState(() => dataEngine.migration.getStatus());
  useEffect(() => {
    const t = setInterval(() => setStatus(dataEngine.migration.getStatus()), 5000);
    return () => clearInterval(t);
  }, []);
  return status;
}

export function useRunMigration() {
  const [state, setState] = useState({ running: false, result: null, error: null });
  const run = useCallback(async (legacyData, { force = false } = {}) => {
    setState({ running: true, result: null, error: null });
    try {
      const result = await dataEngine.migration.migrate(legacyData, { force });
      setState({ running: false, result, error: null });
      return result;
    } catch (e) {
      setState({ running: false, result: null, error: e.message });
      throw e;
    }
  }, []);
  return { ...state, run };
}

// ================================================================
// SECTION 11: ADMIN-SPECIFIC HOOKS
// ================================================================

/** Diff بین دو نسخه */
export function useDiff(oldVersionId, newVersionId) {
  const tick = useEngineTick('registry:version:changed');
  return useMemo(() => {
    if (!oldVersionId || !newVersionId) return null;
    const oldV = dataEngine.version.get(oldVersionId);
    const newV = dataEngine.version.get(newVersionId);
    if (!oldV || !newV) return null;
    return diffEngine.compare(oldV, newV);
  }, [oldVersionId, newVersionId, tick]);
}

/** تحلیل تأثیر */
export function useImpactAnalysis(projectId, diff) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  useEffect(() => {
    if (!projectId || !diff || !diff.ok) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    impactAnalyzer.analyze(projectId, diff)
      .then((res) => { if (!cancelled) setState({ loading: false, data: res, error: null }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, data: null, error: e.message }); });
    return () => { cancelled = true; };
  }, [projectId, diff]);
  return state;
}

/** Audit log */
export function useAuditLog(filter = EMPTY_OBJECT) {
  const tick = useEngineTick('registry:project:changed');
  const key = JSON.stringify(filter);
  return useMemo(
    () => auditLog.list(filter),
    [key, tick]
  );
}

/** rate limit باقی‌مانده */
export function useRateLimit(action) {
  const { user } = useBooksEngine();
  const tick = useEngineTick('registry:request:changed');
  return useMemo(() => {
    if (!user?.id) return Infinity;
    return rateLimiter.peek(user.id, action).remaining;
  }, [user?.id, action, tick]);
}

// ================================================================


/** خواندن legacy data از localStorage برای migration */
function readLegacyData() {
  try {
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    // TEACHERS_DATA و COURSE_DETAILS در فایل‌های .jsx بودن، اینجا نمی‌تونیم import کنیم
    // پس به عنوان ورودی از بیرون گرفته می‌شن. اگه نبود، migration انجام نمی‌شه.
    return window.__LEGACY_BOOKS_DATA__ || null;
  } catch { return null; }
}

/** ترجمه‌ی خطا به پیام فارسی */
function humanizeError(code) {
  const map = {
    // عمومی
    forbidden: 'شما دسترسی به این عملیات را ندارید.',
    rate_limited: 'تعداد درخواست‌های شما زیاد بوده. لطفاً بعداً تلاش کنید.',
    quota_exceeded: 'فضای ذخیره‌سازی پر شده است.',
    idb_quota_exceeded: 'فضای ذخیره‌سازی ویدیو پر شده است.',
    video_too_large: 'حجم ویدیو بیش از حد مجاز است.',
    image_too_large: 'حجم تصویر بیش از حد مجاز است.',
    duplicate_pending_request: 'شما درخواست در انتظار برای این درس دارید.',
    already_teacher_for_subject: 'شما قبلاً برای این کتاب معلم هستید.',
    folder_name_taken: 'این شناسه پوشه قبلاً استفاده شده است.',
    folder_name_locked: 'شناسه پوشه پس از انتشار قابل تغییر نیست.',
    invalid_transition: 'این تغییر وضعیت مجاز نیست.',
    project_not_found: 'پروژه یافت نشد.',
    version_not_found: 'نسخه یافت نشد.',
    request_not_found: 'درخواست یافت نشد.',
    teacher_not_found: 'معلم یافت نشد.',
    not_found: 'مورد یافت نشد.',
    duplicate_id: 'شناسه تکراری است.',
    not_rejected: 'این درخواست رد نشده است.',
    no_pending_version: 'نسخه‌ی در انتظاری وجود ندارد.',
    no_draft: 'پیش‌نویسی وجود ندارد.',
    no_chapters: 'حداقل یک فصل لازم است.',
    cannot_edit_published_version: 'نسخه‌ی منتشرشده قابل ویرایش نیست.',
    invalid_grade_or_subject: 'پایه یا کتاب نامعتبر است.',
    already_pending: 'درخواست بروزرسانی از قبل در انتظار است.',
    project_exists: 'پروژه‌ی مشابه از قبل وجود دارد.',

    // 🔽 CHANGED: رشته (فقط برای پایه‌های ۱۰-۱۲)
    field_required: 'برای پایه‌های متوسطه دوم، انتخاب رشته الزامی است.',
    field_not_allowed: 'برای این پایه، انتخاب رشته معنی ندارد.',
    field_invalid: 'رشته‌ی انتخاب‌شده نامعتبر است.',
    field_locked: 'رشته پس از انتشار قابل تغییر نیست.',

    // 🔽 CHANGED: پایه/درس
    grade_invalid: 'پایه‌ی انتخاب‌شده نامعتبر است.',
    grade_locked: 'پایه پس از انتشار قابل تغییر نیست.',
    grade_required: 'انتخاب پایه الزامی است.',
    subject_invalid: 'کتاب انتخاب‌شده برای این پایه معتبر نیست.',
    subject_required: 'انتخاب کتاب الزامی است.',
    subject_locked: 'کتاب پس از انتشار قابل تغییر نیست.',

    // 🔽 CHANGED: پیام‌های پیشرفته
    project_key_taken: 'برای این پایه/رشته/کتاب قبلاً پروژه‌ای ثبت شده است.',
    version_project_mismatch: 'نسخه با پروژه مطابقت ندارد.',
    duplicate_pending_request_field: 'شما در حال حاضر درخواست در انتظار برای این پایه/رشته/کتاب دارید.',
  };
  return map[code] || code || 'خطای نامشخص';
}

// ================================================================
// SECTION 13: EXPORTS
// ================================================================

export {
  engine, dataEngine, logicEngine,
  REQUEST_STATUS, PROJECT_STATE, VERSION_STATUS,
  GRADE_TITLES, SUBJECT_LABELS, SUBJECTS_BY_GRADE,
  NOTIF_TYPE, AUDIT_ACTION,
  normalizeGrade, normalizeSubject, normalizeField,   // 🔽 CHANGED

  // 🔽 CHANGED: curriculum constants + lookups
  FIELDS, FIELD_LIST, FIELD_IDS, SUBJECTS,
  getGrade, getField, getSubject,
  getSubjectTitle, getBookTitle,
  isHighSchool, needsField,
  getSubjectIdsFor, getSubjectsFor, getStageForGrade,
};



export default {
  // Provider
  BooksEngineProvider,
  // Base
  useBooksEngine, useBooksEngineOptional,
  useEngineTick, usePermission, useCapabilities, useEngineReady,
  // Data
  useTeacherProfile, useTeacherByUserId, useTeacherByFolder,
  useTeachersForSubject, useMyRequests, useRequest, usePendingRequests,
  useMyProjects, useProject, useAllProjects,
  useProjectVersions, useVersion, useBookData, useLessonData,
  useSystemStats,

  useBookLocks,

  // 🆕 Step Quiz unified
  useQuizStepProgress,
  useQuizProgressForLesson,
  useCanAttemptQuiz,

  useProjectSubscription,
  useStepAccess,
  useSubscribeToProject,
  useProjectSubscribers,

    // 🆕 Step Quiz
  useStepQuiz, useStepQuizPass, useStepQuizAccess, useStepQuizCanAttempt,
  // 🔽 CHANGED: curriculum hooks
  useGradeOptions, useGradeOptionsGrouped,
  useFieldOptions, useSubjectOptions,
  useGradeInfo, useFieldInfo, useSubjectInfo,
  useBookTitle, useNeedsField, useStages,
  // Actions
  useAction,
  useTeacherRequestActions, useAdminRequestActions,
  useVersionActions, useProjectActions,
  // Draft editing
  useDraftEditor, useAutoSave, useUndoRedo,
  useKeyboardShortcuts, useBeforeUnload,
  // Media
  useBlobUrl, useBlobMeta, useVideoUpload, useImageUpload,
  // Notifications
  useNotifications, useUnreadCount,
  // System
  useStorageStatus, useMultiTabSync, useOnlineStatus, useManualCleanup,
  // Migration
  useMigrationStatus, useRunMigration,
  // Admin
  useDiff, useImpactAnalysis, useAuditLog, useRateLimit,

  // 🆕 Free Study
  useMyFreeStudyProjects,
  usePendingFreeStudyProjects,
  useAllFreeStudyProjects,
  useFreeStudyEditor,
};